import { ConfigProvider, Flex, Progress } from 'antd';
import React, { useEffect } from 'react';
import { useIsTablet } from '../../hooks/useBreakpoint';
import { Label, RelativeContainer } from './styles';

export type ProgressItem = {
	id: string;
	percent: number;
	progressColor?: string;
	remainingColor?: string;
	finishedColor?: string;
	format: () => React.ReactNode;
};

type Props = {
	items: ProgressItem[];
	label: string;
};

const CustomProgress = ({
	index,
	percent,
	progressColor = '#2875ea',
	remainingColor = '#1677FF33',
	finishedColor = '#52c41a',
	format,
}: ProgressItem & { index: number }) => {
	const isTablet = useIsTablet();
	const [loadDiagram, setLoadDiagram] = React.useState(false);

	useEffect(() => {
		const timeout = setTimeout(
			() => {
				setLoadDiagram(true);
			},
			300 + index * 100,
		);

		return () => {
			clearTimeout(timeout);
		};
	}, []);

	return (
		<ConfigProvider
			theme={{
				components: {
					Progress: {
						remainingColor,
					},
				},
			}}
		>
			<Progress
				type="circle"
				percent={!loadDiagram ? 0 : percent}
				format={format}
				strokeWidth={10}
				strokeColor={percent === 100 ? finishedColor : progressColor}
				size={isTablet ? 100 - index * 25 : 120 - index * 35}
			/>
		</ConfigProvider>
	);
};

export const MultiProgress = ({ items, label }: Props) => {
	const getFormattedInfo = (index: number) => {
		if (index !== items.length - 1) {
			return undefined;
		}

		return (
			<Flex vertical>
				{items.map(item => (
					<Label key={item.id} $success={item.percent === 100} $color={item.progressColor}>
						{item.format()}
					</Label>
				))}
			</Flex>
		);
	};

	return (
		<Flex gap={10} vertical align="center" key={label} data-testid={`multi-progress-${label}`}>
			<RelativeContainer>
				{items.map((item, index) => (
					<CustomProgress key={item.id} index={index} {...item} format={() => getFormattedInfo(index)} />
				))}
			</RelativeContainer>
			<Label>{label}</Label>
		</Flex>
	);
};
